"use client";
import clsx from "clsx";
import { Post } from "contentlayer/generated";
import { useEffect, useRef, useState } from "react";
import { uniq } from "src/lib/utils/uniq";

const ChevronDownIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const notInline =
  ":not(a):not(b):not(label):not(form):not(abbr):not(legend):not(address):not(link):not(area):not(mark):not(audio):not(meter):not(b):not(nav):not(cite):not(optgroup):not(code):not(option):not(del):not(q):not(details):not(small):not(dfn):not(select):not(command):not(source):not(datalist):not(span):not(span *):not(em):not(strong):not(font):not(sub):not(i):not(summary):not(iframe):not(sup):not(img):not(tbody):not(input):not(td):not(ins):not(time):not(kbd):not(var)";
const notCode =
  ":not(pre *):not(.katex-display *):not(svg):not(svg *):not(span *):not(.sticky *):not(.sticky):not(.relative):not(.steps)";

export const TOC = ({
  headings,
  id,
}: {
  id: string;
  headings: Post["headings"];
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver>(null);
  const visibleSet = useRef(new Set<Element>());
  const [currentActive, setCurrentActive] = useState<string[]>();
  const [headerMap, setHeaderMap] = useState(new Map<Element, string>());
  const [isCollapsed, setIsCollapsed] = useState(false);
  const idMap = Object.fromEntries(headings.map((h) => [h.slug, true]));

  useEffect(() => {
    let lastHeader = null;
    const hmap = new Map<Element, string>();
    /* Assumes all children are visible and block, etc. */
    /* go through relevant childrens of the article and associate them with a header */

    const chlds = document.querySelectorAll(`#${id} *${notCode}${notInline}`);
    for (let el of chlds) {
      const id = el.getAttribute("id");
      if (idMap[id || ""]) {
        lastHeader = el.getAttribute("id");
      }
      lastHeader && hmap.set(el, lastHeader);
    }
    setHeaderMap(hmap);
  }, []);

  useEffect(() => {
    if (!headerMap.size || !containerRef.current) return;

    console.log({ isCollapsed });

    /* run with lag, to allow animation to settle */
    setTimeout(() => {
      const chlds = document.querySelectorAll(`#${id} *${notCode}${notInline}`);
      /* Register the children for intersection observation */
      const handleIntersection: IntersectionObserverCallback = (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            visibleSet.current.add(target);
          } else {
            visibleSet.current.delete(target);
          }
        });

        const active = uniq(
          [...visibleSet.current].map((x) => headerMap.get(x)),
        );
        setCurrentActive(active);
        if (!active.length) return;
        const res = containerRef.current?.querySelectorAll<HTMLElement>(
          active.map((x) => `#toc-item-${x}`).join(","),
        );
        if (!res?.length) return;
        const rect = svgRef.current?.getElementsByTagName("rect")[0];
        const top = Math.min(...[...res].map((x) => x.offsetTop));
        const bottom = Math.max(
          ...[...res].map((x) => x.offsetTop + x.offsetHeight),
        );
        rect?.setAttribute("y", `${top}px`);
        /* rect?.setAttribute("width", "100%"); */
        rect?.setAttribute("height", `${bottom - top}px`);
      };

      observer.current = new IntersectionObserver(handleIntersection, {
        threshold: 0,
      });
      chlds.forEach((el) => observer.current?.observe(el));
    }, 300);
    return () => observer.current?.disconnect();
  }, [headerMap, isCollapsed]);

  const getVisibleHeadings = () => {
    if (!isCollapsed) return headings;

    const visibleHeadings = new Set<string>();

    // Always show top level headings (level 2)
    headings.forEach((heading) => {
      if (heading.level === 2) {
        visibleHeadings.add(heading.slug);
      }
    });

    if (currentActive && currentActive.length > 0) {
      // Find the deepest active heading (leaf of current path)
      const activeHeadings = currentActive
        .map((slug) => headings.find((h) => h.slug === slug))
        .filter(Boolean);
      const deepestActive = activeHeadings?.reduce((deepest, current) =>
        current && deepest && current.level > deepest.level ? current : deepest,
      );

      if (deepestActive) {
        // Add all headings in the path to the deepest active heading
        const pathSlugs = getPathToHeading(headings, deepestActive.slug);
        pathSlugs.forEach((slug) => visibleHeadings.add(slug));

        // Add all siblings at the deepest active level
        const siblings = getSiblingsOfHeading(headings, deepestActive.slug);
        siblings.forEach((sibling) => visibleHeadings.add(sibling.slug));
      }
    }

    return headings.filter((heading) => visibleHeadings.has(heading.slug));
  };

  const getPathToHeading = (
    headings: Post["headings"],
    targetSlug: string,
  ): string[] => {
    const targetIndex = headings.findIndex((h) => h.slug === targetSlug);
    if (targetIndex === -1) return [];

    const targetLevel = headings[targetIndex].level;
    const path: string[] = [];

    // Work backwards from target to find parent path
    for (let level = targetLevel - 1; level >= 2; level--) {
      for (let i = targetIndex - 1; i >= 0; i--) {
        if (headings[i].level === level) {
          // This is a potential parent - verify it's actually a parent
          if (isParentOf(headings, i, targetIndex)) {
            path.unshift(headings[i].slug);
            break;
          }
        }
      }
    }

    return path;
  };

  const getSiblingsOfHeading = (
    headings: Post["headings"],
    targetSlug: string,
  ): Post["headings"] => {
    const targetIndex = headings.findIndex((h) => h.slug === targetSlug);
    if (targetIndex === -1) return [];

    const targetLevel = headings[targetIndex].level;
    const parentIndex = findParentIndex(headings, targetIndex);
    const nextParentIndex = findNextParentIndex(
      headings,
      targetIndex,
      targetLevel,
    );

    const siblings: Post["headings"] = [];

    for (
      let i = Math.max(0, parentIndex + 1);
      i < headings.length && i < nextParentIndex;
      i++
    ) {
      if (headings[i].level === targetLevel) {
        siblings.push(headings[i]);
      }
    }

    return siblings;
  };

  const isParentOf = (
    headings: Post["headings"],
    parentIndex: number,
    childIndex: number,
  ): boolean => {
    const parentLevel = headings[parentIndex].level;

    // Check if there's any heading between parent and child that would break the parent-child relationship
    for (let i = parentIndex + 1; i < childIndex; i++) {
      if (headings[i].level <= parentLevel) {
        return false;
      }
    }

    return true;
  };

  const findParentIndex = (
    headings: Post["headings"],
    currentIndex: number,
  ): number => {
    const currentLevel = headings[currentIndex].level;
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (headings[i].level < currentLevel) {
        return i;
      }
    }
    return -1;
  };

  const findNextParentIndex = (
    headings: Post["headings"],
    currentIndex: number,
    level: number,
  ): number => {
    for (let i = currentIndex + 1; i < headings.length; i++) {
      if (headings[i].level < level) {
        return i;
      }
    }
    return headings.length;
  };

  return (
    <aside
      ref={containerRef}
      className="sticky top-8 hidden max-w-[14em] 2xl:col-start-1  2xl:block h-fit max-h-[90vh] overflow-y-auto pr-12 mt-12 bg-opacity-70 bg-theme-bg-main"
    >
      <div className="relative *:mx-2 border-r dark:border-r-slate-700 space-y-2  text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200">
        <svg
          ref={svgRef}
          className="absolute h-full top-0 left-0 -z-10 mx-0.5! m-auto"
          width="97%"
        >
          <rect
            rx="5"
            width="2"
            className="stroke-1 stroke-neutral-300 fill-neutral-300  dark:stroke-slate-400  dark:fill-slate-400"
            style={{
              transition: "y 0.5s, height 0.5s",
            }}
          ></rect>
        </svg>
        <div className="flex items-center justify-between mb-2 ml-0!">
          <p className="text-gray-500">Table of Contents</p>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-all duration-200 ease-in-out"
            aria-label={
              isCollapsed ? "Expand all sections" : "Collapse all sections"
            }
            title={
              isCollapsed ? "Expand all sections" : "Collapse all sections"
            }
          >
            <div
              className="transition-transform duration-200 ease-in-out"
              style={{
                transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              }}
            >
              <ChevronDownIcon />
            </div>
          </button>
        </div>
        {headings.map((heading) => {
          const isActive = currentActive?.includes(heading.slug);
          const isVisible = getVisibleHeadings().some(
            (h) => h.slug === heading.slug,
          );

          return (
            <a
              id={`toc-item-${heading.slug}`}
              key={`#${heading.slug}`}
              className={clsx(
                "block text-xs leading-normal tracking-wide no-underline transition-all duration-300 ease-in-out overflow-hidden",
                isActive && "text-gray-600 dark:text-gray-400",
                isVisible
                  ? "opacity-100 max-h-8 mb-2"
                  : "opacity-0 max-h-0 mb-0",
              )}
              style={{
                paddingLeft: `${(heading.level - 2) * 25}px`,
              }}
              data-level={heading.level}
              href={`#${heading.slug}`}
            >
              <span
                className={clsx(
                  heading.level === 2 &&
                  `decoration-solid font-bold dark:text-gray-300 dark:decoration-gray-400 ${isActive
                    ? "text-gray-600 decoration-gray-500"
                    : "text-gray-500 decoration-gray-400"
                  }`,
                )}
              >
                {heading.text}{" "}
              </span>
            </a>
          );
        })}
      </div>
    </aside>
  );
};
